import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { useTenant } from '@/services/tenant-context';
import { quotesApi, proposalsApi } from '@/services/commercial-docs-api';
import { priceBooksApi, rateCardsApi, remindersApi } from '@/services/catalogs-api';
import type { KoshQuoteStatus, KoshProposalStatus } from '@/types/commercial-docs';
import type { KoshCatalogStatus, KoshReminderStatus } from '@/types/catalogs';

function useScope(): string {
  return useTenant().currentTenant.id;
}
function useInvalidate() {
  const qc = useQueryClient();
  return (keys: QueryKey[]) => keys.forEach((k) => qc.invalidateQueries({ queryKey: k }));
}

// ── Quotes ──────────────────────────────────────────────────────────────────
export function useQuotes() {
  const s = useScope();
  return useQuery({ queryKey: ['kosh', 'quotes', s], queryFn: () => quotesApi.list({ pageSize: 200 }) });
}
export function useQuote(id: string | undefined) {
  const s = useScope();
  return useQuery({ queryKey: ['kosh', 'quotes', s, id], queryFn: () => quotesApi.get(id!), enabled: Boolean(id) });
}
export function useQuoteTransition() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ id, to }: { id: string; to: KoshQuoteStatus }) => quotesApi.transition(id, to),
    onSuccess: () => inv([['kosh', 'quotes']]),
  });
}

// ── Proposals ────────────────────────────────────────────────────────────────
export function useProposals() {
  const s = useScope();
  return useQuery({ queryKey: ['kosh', 'proposals', s], queryFn: () => proposalsApi.list({ pageSize: 200 }) });
}
export function useProposal(id: string | undefined) {
  const s = useScope();
  return useQuery({ queryKey: ['kosh', 'proposals', s, id], queryFn: () => proposalsApi.get(id!), enabled: Boolean(id) });
}
export function useProposalTransition() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ id, to }: { id: string; to: KoshProposalStatus }) => proposalsApi.transition(id, to),
    onSuccess: () => inv([['kosh', 'proposals']]),
  });
}

// ── Price books ──────────────────────────────────────────────────────────────
export function usePriceBooks() {
  const s = useScope();
  return useQuery({ queryKey: ['kosh', 'price-books', s], queryFn: () => priceBooksApi.list({ pageSize: 200 }) });
}
export function usePriceBook(id: string | undefined) {
  const s = useScope();
  return useQuery({ queryKey: ['kosh', 'price-books', s, id], queryFn: () => priceBooksApi.get(id!), enabled: Boolean(id) });
}
export function usePriceBookTransition() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ id, to }: { id: string; to: KoshCatalogStatus }) => priceBooksApi.transition(id, to),
    onSuccess: () => inv([['kosh', 'price-books']]),
  });
}

// ── Rate cards ───────────────────────────────────────────────────────────────
export function useRateCards() {
  const s = useScope();
  return useQuery({ queryKey: ['kosh', 'rate-cards', s], queryFn: () => rateCardsApi.list({ pageSize: 200 }) });
}
export function useRateCard(id: string | undefined) {
  const s = useScope();
  return useQuery({ queryKey: ['kosh', 'rate-cards', s, id], queryFn: () => rateCardsApi.get(id!), enabled: Boolean(id) });
}
export function useRateCardTransition() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ id, to }: { id: string; to: KoshCatalogStatus }) => rateCardsApi.transition(id, to),
    onSuccess: () => inv([['kosh', 'rate-cards']]),
  });
}

// ── Reminders ────────────────────────────────────────────────────────────────
export function useReminders() {
  const s = useScope();
  return useQuery({ queryKey: ['kosh', 'reminders-list', s], queryFn: () => remindersApi.list({ pageSize: 200 }) });
}
export function useReminderTransition() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ id, to }: { id: string; to: KoshReminderStatus }) => remindersApi.transition(id, to),
    onSuccess: () => inv([['kosh', 'reminders-list']]),
  });
}
